/* ==================================================================
 * CORSICA TIME PORTAL — CONFIG
 * Everything you'll want to tune lives in this file.
 * ================================================================== */

/* ---- FRAME SEQUENCE ---------------------------------------------- */

export const FRAME_COUNT = 258;

export const framePath = (i) =>
  `${process.env.PUBLIC_URL}/frames/modern/ezgif-frame-${String(i + 1).padStart(3, "0")}.jpg`;

export const SCROLL_HEIGHT_VH = 700;
export const LOAD_CONCURRENCY = 8;

/* ---- PACING -------------------------------------------------------
 * PORTAL_FRAME  first frame where the portal starts appearing (1-indexed)
 * MODERN_SHARE  fraction of total scroll given to the modern walk
 * ------------------------------------------------------------------ */

export const PORTAL_FRAME = 77;
export const MODERN_SHARE = 0.62;

export function frameAtProgress(p, frameCount) {
  const pivot = Math.max(0, Math.min(PORTAL_FRAME - 1, frameCount - 1));
  const share = Math.max(0.01, Math.min(0.99, MODERN_SHARE));

  if (p <= share) return (p / share) * pivot;
  return pivot + ((p - share) / (1 - share)) * (frameCount - 1 - pivot);
}

/* ---- TIME --------------------------------------------------------- */

export const YEARS_BACK = 100;

const LOCALES = { en: "en-GB", fr: "fr-FR", it: "it-IT" };

export function dateStrings(lang = "en") {
  const now = new Date();
  const then = new Date(now);
  then.setFullYear(now.getFullYear() - YEARS_BACK);

  const fmt = new Intl.DateTimeFormat(LOCALES[lang] || "en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return {
    today: fmt.format(now),
    past: fmt.format(then),
    todayYear: now.getFullYear(),
    pastYear: then.getFullYear(),
  };
}

export const STORY_YEAR = String(new Date().getFullYear() - YEARS_BACK);

/* ---- HISTORICAL MONEY ---------------------------------------------
 * NOTE: the 3 francs ≈ €2.50 figure in dialogue.js is a placeholder.
 * Interwar French franc purchasing power is messy — the franc was
 * devalued hard through the 1920s — so get a real figure before launch.
 * ------------------------------------------------------------------ */

/* ---- LOCKBOX ------------------------------------------------------
 * DEMO ONLY. The code is generated in the browser and shown on screen.
 *
 * Real version:
 *   name + email  →  POST to backend
 *   backend generates the code, stores it against the game session
 *                 with a short expiry, emails it
 *   player enters the code on the dials
 *   backend verifies code + session + that the previous checkpoint and
 *                 payment are complete
 *   backend returns the photo URL
 *
 * The photo must never ship to the client before verification, or the
 * whole gate is decorative.
 * ------------------------------------------------------------------ */

export const CODE_LENGTH = 6;

/* ---- ARTWORK ------------------------------------------------------ */

export const LETTER_IMAGE = `${process.env.PUBLIC_URL}/media/letter.jpg`;

// Drawings characters hand over. A node in dialogue.js sets
// sketch: "lockbox" and this resolves it.
export const sketchPath = (name) =>
  `${process.env.PUBLIC_URL}/media/clues/${name}.jpg`;

// Photographs found inside things. Drop the Citadel photo in as
// public/media/clues/citadel-photo.jpg
export const photoPath = (name) =>
  `${process.env.PUBLIC_URL}/media/clues/${name}.jpg`;

/* ---- ENDING ------------------------------------------------------- */

export const ENDING_START = 0.93;

/* ---- SCROLL BEATS ------------------------------------------------- */

export const BEATS = [
  {
    at: [0.03, 0.17],
    kicker: "Porto-Vecchio",
    line: "You are standing where he stood.",
  },
  {
    at: [0.2, 0.34],
    kicker: null,
    line: "He wrote that this street remembers. That it never stopped remembering.",
  },
  {
    at: [0.37, 0.5],
    kicker: null,
    line: "A hundred years ago a man walked this exact stretch carrying something he did not want found.",
  },
  {
    at: [0.53, 0.63],
    kicker: null,
    line: "Stop here.",
  },
];

/* ---- COPY --------------------------------------------------------- */

export const COPY = {
  en: {
    label: "EN",

    // letter
    found: "You found it.",
    tapLetter: "Tap the letter",
    letterBody: [
      "If you are reading this, then I did not come back for it.",
      "Someone hid something in Porto-Vecchio. I know roughly where, and I know who put it there — but he has been dead a hundred years, and he was not inclined to write it down.",
      "His name was Ghjaseppu. Ask anyone here and you will get a different answer about him.",
      "The seal below is not decoration. Break it, and the street outside will not be the only street you can see.",
    ],
    activateSeal: "Break the seal",

    // walk
    connecting: "Establishing the connection",
    scroll: "scroll",
    today: "Today",
    endLine: "There is a man at the corner, watching you.",
    endInstruction:
      "He can see you. Find the mark near the basilica and hold your camera to it.",
    endCta: "Find the mark",

    // qr gate — shared
    qrDemoNote: "Demo — the real scanner replaces this once the codes are printed",
    qrDemoCta: "Skip the scan",

    // qr gate — basilica
    qr1Title: "Find the mark",
    qr1Body:
      "There is a mark near the basilica, on the wall by the door. Hold your camera to it and he will notice you.",

    // qr gate — genoese gate
    qr2Title: "Past the old gate",
    qr2Body:
      "Follow Rue du Port de Gênes down toward the water. Go under the gate. The mark is on the stone, close to where he drew the box.",

    // dialogue
    continue: "Continue",
    francs: "francs",
    translateCta: "What is that today?",
    todayApprox: "Roughly, today",
    payCta: "Pay",
    demoPayNote: "Demo — no payment is taken",

    // lockbox
    boxFound: "It is exactly where he drew it.",
    boxLocked: "Six numbers. No keyhole.",
    boxWhoAsk: "Ask for the numbers",
    formIntro:
      "I left the box. Tell me who found it and I will send you the combination.",
    formName: "First name",
    formEmail: "Email",
    formSubmit: "Send the numbers",
    formInvalidEmail: "That email doesn't look right.",
    formPrivacy:
      "Your name and email are used to send the combination and nothing else.",
    codeSentTitle: "Check your email",
    codeSentBody: "Six numbers. Set them on the dials.",
    codeDemoNote: "Demo — no email is sent. The code is:",
    codeGoToBox: "Go to the box",
    dialsHint: "Tap above or below a number to turn it",
    boxOpening: "Something gives.",
  },

  fr: {
    label: "FR",

    found: "Vous l'avez trouvée.",
    tapLetter: "Touchez la lettre",
    letterBody: [
      "Si vous lisez ceci, c'est que je ne suis pas revenu la chercher.",
      "Quelqu'un a caché quelque chose à Porto-Vecchio. Je sais à peu près où, et je sais qui l'y a mis — mais il est mort depuis cent ans, et il n'était pas homme à l'écrire.",
      "Il s'appelait Ghjaseppu. Demandez à n'importe qui ici et vous aurez une réponse différente à son sujet.",
      "Le sceau ci-dessous n'est pas un ornement. Brisez-le, et la rue dehors ne sera plus la seule rue que vous puissiez voir.",
    ],
    activateSeal: "Briser le sceau",

    connecting: "Établissement de la connexion",
    scroll: "faites défiler",
    today: "Aujourd'hui",
    endLine: "Il y a un homme au coin de la rue. Il vous regarde.",
    endInstruction:
      "Il vous voit. Trouvez la marque près de la basilique et dirigez votre caméra dessus.",
    endCta: "Trouver la marque",

    qrDemoNote:
      "Démo — le vrai scanner remplacera ceci une fois les codes imprimés",
    qrDemoCta: "Passer le scan",

    qr1Title: "Trouver la marque",
    qr1Body:
      "Il y a une marque près de la basilique, sur le mur à côté de la porte. Dirigez votre caméra dessus et il vous remarquera.",

    qr2Title: "Après la vieille porte",
    qr2Body:
      "Suivez la Rue du Port de Gênes vers l'eau. Passez sous la porte. La marque est sur la pierre, tout près de l'endroit qu'il a dessiné.",

    continue: "Continuer",
    francs: "francs",
    translateCta: "Combien cela fait aujourd'hui ?",
    todayApprox: "Environ, aujourd'hui",
    payCta: "Payer",
    demoPayNote: "Démo — aucun paiement n'est prélevé",

    boxFound: "Elle est exactement là où il l'a dessinée.",
    boxLocked: "Six chiffres. Aucune serrure.",
    boxWhoAsk: "Demander les chiffres",
    formIntro:
      "C'est moi qui ai laissé cette boîte. Dites-moi qui l'a trouvée et je vous enverrai la combinaison.",
    formName: "Prénom",
    formEmail: "Email",
    formSubmit: "Envoyer les chiffres",
    formInvalidEmail: "Cet email ne semble pas valide.",
    formPrivacy:
      "Votre prénom et votre email servent uniquement à vous envoyer la combinaison.",
    codeSentTitle: "Regardez vos emails",
    codeSentBody: "Six chiffres. Réglez-les sur les molettes.",
    codeDemoNote: "Démo — aucun email n'est envoyé. Le code est :",
    codeGoToBox: "Retourner à la boîte",
    dialsHint: "Touchez au-dessus ou en dessous d'un chiffre pour le tourner",
    boxOpening: "Quelque chose cède.",
  },

  it: {
    label: "IT",

    found: "L'hai trovata.",
    tapLetter: "Tocca la lettera",
    letterBody: [
      "Se stai leggendo questo, allora non sono tornato a riprenderla.",
      "Qualcuno ha nascosto qualcosa a Porto-Vecchio. So all'incirca dove, e so chi ce l'ha messo — ma è morto da cento anni, e non era tipo da metterlo per iscritto.",
      "Si chiamava Ghjaseppu. Chiedi a chiunque qui e avrai una risposta diversa su di lui.",
      "Il sigillo qui sotto non è un ornamento. Spezzalo, e la strada là fuori non sarà più l'unica strada che puoi vedere.",
    ],
    activateSeal: "Spezza il sigillo",

    connecting: "Connessione in corso",
    scroll: "scorri",
    today: "Oggi",
    endLine: "C'è un uomo all'angolo. Ti sta guardando.",
    endInstruction:
      "Ti vede. Trova il segno vicino alla basilica e puntaci la fotocamera.",
    endCta: "Trova il segno",

    qrDemoNote:
      "Demo — il vero scanner sostituirà questo una volta stampati i codici",
    qrDemoCta: "Salta la scansione",

    qr1Title: "Trova il segno",
    qr1Body:
      "C'è un segno vicino alla basilica, sul muro accanto alla porta. Puntaci la fotocamera e lui ti noterà.",

    qr2Title: "Oltre la vecchia porta",
    qr2Body:
      "Segui Rue du Port de Gênes verso l'acqua. Passa sotto la porta. Il segno è sulla pietra, vicino al punto che ha disegnato.",

    continue: "Continua",
    francs: "franchi",
    translateCta: "Quanto fa oggi?",
    todayApprox: "Circa, oggi",
    payCta: "Paga",
    demoPayNote: "Demo — nessun pagamento viene effettuato",

    boxFound: "È esattamente dove l'aveva disegnata.",
    boxLocked: "Sei numeri. Nessuna serratura.",
    boxWhoAsk: "Chiedi i numeri",
    formIntro:
      "La scatola l'ho lasciata io. Dimmi chi l'ha trovata e ti manderò la combinazione.",
    formName: "Nome",
    formEmail: "Email",
    formSubmit: "Mandami i numeri",
    formInvalidEmail: "Questa email non sembra valida.",
    formPrivacy:
      "Il tuo nome e la tua email servono solo per inviarti la combinazione.",
    codeSentTitle: "Controlla la tua email",
    codeSentBody: "Sei numeri. Impostali sulle ghiere.",
    codeDemoNote: "Demo — nessuna email viene inviata. Il codice è:",
    codeGoToBox: "Torna alla scatola",
    dialsHint: "Tocca sopra o sotto un numero per girarlo",
    boxOpening: "Qualcosa cede.",
  },
};

export function detectLanguage() {
  const nav = (navigator.language || "en").slice(0, 2).toLowerCase();
  return COPY[nav] ? nav : "en";
}
